import { Component, type ErrorInfo, type ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2 } from "lucide-react";

/**
 * Render a privacy-safe representation of a render-time error for the
 * fallback card. In dev we expose the full message + truncated stack so
 * developers can debug quickly. In production we deliberately strip
 * `error.message` — it can contain user identifiers, decrypted text, or
 * sensitive API payload fragments — and surface only the error CLASS
 * (e.g. "TypeError", "ChunkLoadError") plus the first 3 stack frames'
 * file:line locations with any query-strings removed. That's enough to
 * triage an iOS-only crash from a user-supplied screenshot without
 * leaking PHI.
 */
function renderRedactedDetails(error: Error): string {
  if (import.meta.env.DEV) {
    return (
      String(error.message || error) +
      (error.stack ? "\n\n" + error.stack.split("\n").slice(0, 6).join("\n") : "")
    );
  }
  const name = error.name || "Error";
  const frames = (error.stack || "")
    .split("\n")
    .slice(1, 4)
    .map((line) => {
      const m = line.match(/([^/\\\s]+\.(?:t|j)sx?):(\d+)(?::(\d+))?/);
      if (m) return `  at ${m[1]}:${m[2]}${m[3] ? ":" + m[3] : ""}`;
      return "  at (unknown frame)";
    })
    .join("\n");
  return `${name}\n${frames || "  (no stack frames available)"}`;
}

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Friendly label shown in the default fallback (e.g. "Journal"). */
  label?: string;
  /**
   * Task #53 — when this value changes (e.g. the current route), any caught
   * error is cleared and the retry budget restored, so a one-time glitch on
   * one page doesn't stick to the error card after the user navigates away.
   */
  resetKey?: unknown;
}

interface State {
  error: Error | null;
  showDetails: boolean;
  /**
   * True while we are silently auto-retrying a caught error (no visible
   * blocking card yet). False means the error is being surfaced to the user
   * because automatic recovery was exhausted.
   */
  recovering: boolean;
}

/**
 * How many times we silently re-render the children after a caught error
 * before giving up and showing the visible "Try again" card.
 */
const MAX_AUTO_RETRIES = 3;
/** Base delay between silent retries; scales up slightly with each attempt. */
const RETRY_BACKOFF_MS = 200;

/**
 * Defensive React error boundary. Used to keep a single bad render path from
 * taking down the whole page (Task #130 — Journal previously crashed the app
 * when an unexpected shape arrived from the journal API or the decrypt path
 * threw mid-render).
 *
 * Self-healing (Task #222): some render throws are transient — most notably
 * the brief commit-timing race the dashboard can hit on the first render right
 * after the vault unlocks, where a plain "Try again" always succeeds. Rather
 * than make the user click that button, the boundary now silently resets and
 * re-renders the children a bounded number of times. The user sees, at most, a
 * brief loader. The visible "Something went wrong" card is reserved for the
 * genuinely persistent case where automatic recovery is exhausted. The retry
 * budget is restored the instant a re-render succeeds (see `componentDidUpdate`),
 * so a later, unrelated transient error always gets a full, fresh set of
 * silent retries rather than inheriting a depleted budget.
 *
 * Root-cause note (Task #222): this self-healing behavior is the guaranteed,
 * in-scope fix. A best-effort root-cause investigation found the dashboard's
 * post-unlock render path (Home + useEncryptedReflection) already well-guarded
 * (Array.isArray / nullish-coalescing fallbacks, no unguarded date/JSON parse),
 * and the symptom matches the known React commit-timing race around the vault
 * `unlockVault()` notify (already mitigated with a microtask defer in
 * `client/src/lib/vault/vaultState.ts`; further changes to that crypto/unlock
 * flow are out of scope here). Precise decoding of the reported minified frames
 * was infeasible — the user's production source maps weren't shipped and a local
 * rebuild produces different bundle hashes/line numbers, so e.g. iPhone
 * `38:19152` cannot be mapped back to a source location. Follow-up #223 adds
 * anonymity-safe error reporting so the cause can be pinned from real telemetry.
 *
 * The card itself is intentionally minimal and non-leaky: we never render the
 * raw error message to the user (it could contain identifiers), only a
 * "something went wrong" card with a Try again action that resets the
 * boundary's internal state. The full error is logged to the console for
 * developers.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, showDetails: false, recovering: false };

  /** Number of silent retries used since the last clean run. */
  private retryCount = 0;
  /** Pending silent-retry timer. */
  private resetTimer: ReturnType<typeof setTimeout> | null = null;

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Optimistically assume we'll silently recover so the user never sees the
    // card flash before `componentDidCatch` decides. If the retry budget is
    // exhausted, `componentDidCatch` flips `recovering` to false.
    return { error, showDetails: false, recovering: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] caught render error:", error, info);

    if (this.retryCount < MAX_AUTO_RETRIES) {
      this.retryCount += 1;
      const delay = RETRY_BACKOFF_MS * this.retryCount;
      this.clearResetTimer();
      this.resetTimer = setTimeout(this.attemptSilentRecover, delay);
    } else {
      // Budget exhausted — surface the visible card.
      this.setState({ recovering: false });
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    // Task #53 — navigating to a different route is a fresh start: clear any
    // sticky error and restore the full silent-retry budget.
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.reset();
      return;
    }
    // A render just SUCCEEDED if we transitioned from "had an error" to
    // "no error" and the children committed without throwing again (a throw
    // during the recovery render would have re-set `error` via
    // getDerivedStateFromError before this commit, so we'd still see an error
    // here). Restore the full retry budget immediately so a later, unrelated
    // transient error gets a fresh set of silent retries — not the leftovers.
    if (prevState.error && !this.state.error) {
      this.retryCount = 0;
    }
  }

  componentWillUnmount() {
    this.clearResetTimer();
  }

  private clearResetTimer() {
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
      this.resetTimer = null;
    }
  }

  /** Silently clear the error and re-mount the children to retry the render. */
  private attemptSilentRecover = () => {
    this.resetTimer = null;
    // Clearing the error re-mounts the children. If they render cleanly,
    // `componentDidUpdate` restores the retry budget; if they throw again,
    // `componentDidCatch` fires and schedules the next bounded retry.
    this.setState({ error: null, showDetails: false, recovering: false });
  };

  /** Manual reset from the visible card — full reset of the retry budget. */
  private reset = () => {
    this.clearResetTimer();
    this.retryCount = 0;
    this.setState({ error: null, showDetails: false, recovering: false });
  };

  private toggleDetails = () => {
    this.setState((s) => ({ showDetails: !s.showDetails }));
  };

  render() {
    if (this.state.error) {
      // Silent auto-recovery in flight — show at most an unobtrusive loader so
      // the surrounding app chrome (sidebar/header) stays put and the user
      // doesn't see a blocking error card for a transient hiccup.
      if (this.state.recovering) {
        return (
          <div
            className="flex items-center justify-center p-8"
            data-testid="status-error-recovering"
            aria-busy="true"
          >
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        );
      }

      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      const label = this.props.label ?? "this page";
      return (
        <div className="p-4">
          <Card className="max-w-lg mx-auto p-6 space-y-4 text-center">
            <div className="flex justify-center">
              <AlertTriangle className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2
              className="text-lg font-semibold"
              data-testid="text-error-boundary-title"
            >
              Something went wrong loading {label}
            </h2>
            <p className="text-sm text-muted-foreground">
              We caught the problem before it crashed the app. You can try
              again, and the rest of ForgeRestore stays available in the sidebar.
            </p>
            <div className="flex justify-center gap-2 flex-wrap">
              <Button
                onClick={this.reset}
                data-testid="button-error-boundary-retry"
              >
                Try again
              </Button>
              <Button
                variant="outline"
                onClick={this.toggleDetails}
                data-testid="button-error-boundary-details"
              >
                {this.state.showDetails ? "Hide details" : "Show details"}
              </Button>
            </div>
            {this.state.showDetails && (
              <div
                className="mt-2 rounded-md border bg-muted/50 p-3 text-left text-xs font-mono whitespace-pre-wrap break-words max-h-48 overflow-auto"
                data-testid="text-error-boundary-details"
              >
                {renderRedactedDetails(this.state.error)}
              </div>
            )}
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}
